import { gql, useQuery } from '@apollo/client';
import { useMemo } from 'react';

import { GraphRef } from 'src/app/graph/hooks/useGraphRef';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

export const useFetchUpstreamSourceVariantSdlsBySubgraph = ({
  graphRef,
  shouldPullUpstreamChanges,
  mergeBaseCompositionId,
  sourceGraphVariant,
}: {
  graphRef: GraphRef;
  shouldPullUpstreamChanges: boolean;
  mergeBaseCompositionId: string | null | undefined;
  sourceGraphVariant: string | undefined;
}) => {
  const { data, loading } = useQuery<
    GraphQLTypes.UseFetchUpstreamSourceVariantSdlsQuery,
    GraphQLTypes.UseFetchUpstreamSourceVariantSdlsQueryVariables
  >(
    gql`
      query UseFetchUpstreamSourceVariantSdlsQuery(
        $graphId: ID!
        $graphVariant: String!
        $launchId: ID!
      ) {
        service(id: $graphId) {
          id
          variant(name: $graphVariant) {
            id
            ... on GraphVariant {
              id
              baseLaunch: launch(id: $launchId) {
                id
                buildInput {
                  ... on CompositionBuildInput {
                    subgraphs {
                      name
                      hash
                      sdl(graphId: $graphId)
                    }
                  }
                }
              }
              latestLaunch {
                id
                buildInput {
                  ... on CompositionBuildInput {
                    subgraphs {
                      name
                      hash
                      sdl(graphId: $graphId)
                    }
                  }
                }
              }
            }
          }
        }
      }
    `,
    {
      skip:
        !shouldPullUpstreamChanges ||
        !mergeBaseCompositionId ||
        !sourceGraphVariant,
      variables: {
        graphId: graphRef.graphId,
        graphVariant: sourceGraphVariant as string, // skipped if not defined
        launchId: mergeBaseCompositionId as string, // skipped if not defined
      },
    },
  );

  const latestSourceVariantBuildInputBySubgraph = useMemo(() => {
    const latestSourceVariantBuildInput =
      data?.service?.variant?.latestLaunch?.buildInput.__typename ===
      'CompositionBuildInput'
        ? data?.service?.variant?.latestLaunch?.buildInput
        : undefined;

    return latestSourceVariantBuildInput?.subgraphs.reduce(
      (acc, subgraph) => ({
        ...acc,
        [subgraph.name]: subgraph,
      }),
      {} as Record<
        string,
        (typeof latestSourceVariantBuildInput)['subgraphs'][number]
      >,
    );
  }, [data?.service?.variant?.latestLaunch?.buildInput]);

  const baseSourceVariantBuildInputBySubgraph = useMemo(() => {
    const sourceVariantMergeBaseBuildInput =
      data?.service?.variant?.baseLaunch?.buildInput.__typename ===
      'CompositionBuildInput'
        ? data?.service?.variant?.baseLaunch?.buildInput
        : undefined;
    return sourceVariantMergeBaseBuildInput?.subgraphs.reduce(
      (acc, subgraph) => ({
        ...acc,
        [subgraph.name]: subgraph,
      }),
      {} as Record<
        string,
        (typeof sourceVariantMergeBaseBuildInput)['subgraphs'][number]
      >,
    );
  }, [data?.service?.variant?.baseLaunch?.buildInput]);

  return {
    latestSourceVariantBuildInputBySubgraph,
    baseSourceVariantBuildInputBySubgraph,
    loading,
  };
};
